import React, { type FC } from 'react'
import { createPortal } from 'react-dom'

import { useIntl } from 'react-intl'

import { useRecoilCallback, useRecoilValue } from 'recoil'

import styled from '@emotion/styled'

import {
  Checkbox,
  ContextMenu,
  Flex,
  LINK_VARIANT_EXTRALIGHT,
  Link,
  makeButtonWithMenu,
} from 'mailup-web-components'

import IconAngleDownSolid from 'Components/icons/IconAngleDownSolid'

import {
  isSelectAllCheckedState,
  isSelectAllIndeterminateState,
  isSelectRecipientDisabled,
} from 'Pages/RecipientsList/recoil/state/results'

import {
  bulkSelectAllCallback,
  bulkSelectNoneCallback,
  bulkSelectVisibleCallback,
} from 'Pages/RecipientsList/recoil/callbacks/bulk'

const StyledChecbox = styled(Checkbox)`
  pointer-events: none;
`

const TriggerComponent: FC = () => {
  const checked = useRecoilValue(isSelectAllCheckedState)
  const disabled = useRecoilValue(isSelectRecipientDisabled)
  const isIndeterminate = useRecoilValue(isSelectAllIndeterminateState)

  const handleChange = (e: any): void => {
    e.stopPropagation()
    e.preventDefault()
  }

  return (
    <Flex alignItems="center" justifyContent="center" gap="4px">
      <StyledChecbox
        checked={checked}
        disabled={disabled}
        isIndeterminate={isIndeterminate}
        data-mu-test="select-all"
        onChange={handleChange}
      />
      <Link variant={LINK_VARIANT_EXTRALIGHT} alignContent>
        <IconAngleDownSolid width="11px" height="12px" />
      </Link>
    </Flex>
  )
}

const styledOpen = ({ $open }: { $open: boolean }): string =>
  $open ? 'opacity: 1; transform: translateY(0);' : 'pointer-events: none;'

const StyledContainer: any = styled.div`
  position: absolute;
  top: 32px;
  left: 0px;
  z-index: 2000;

  opacity: 0;

  transition: all 0.2s ease-out;
  transform: translateY(0.8em);

  ${styledOpen}
`

interface MenuComponentProps {
  open: boolean
}

const MenuComponent: FC<MenuComponentProps> = ({ open }) => {
  const intl = useIntl()

  const bulkSelectAll = useRecoilCallback(bulkSelectAllCallback, [])
  const bulkSelectVisible = useRecoilCallback(bulkSelectVisibleCallback, [])
  const bulkSelectNone = useRecoilCallback(bulkSelectNoneCallback, [])

  const portalEl = document.querySelector('.p-recipients-list-portal')

  if (portalEl === null) {
    return null
  }

  const handleAll = (v: string, e: React.MouseEvent<HTMLLinkElement>): void => {
    e.stopPropagation()

    void bulkSelectAll()
  }

  const handleVisible = (
    v: string,
    e: React.MouseEvent<HTMLLinkElement>,
  ): void => {
    e.stopPropagation()

    void bulkSelectVisible()
  }

  const handleNone = (
    v: string,
    e: React.MouseEvent<HTMLLinkElement>,
  ): void => {
    e.stopPropagation()

    void bulkSelectNone()
  }

  return createPortal(
    <StyledContainer $open={open}>
      <ContextMenu
        items={[
          {
            label: intl.formatMessage({
              id: 'Load.RecipientsList.BatchSelectAll',
            }),
            value: 'all',
            onClick: handleAll,
          },
          {
            label: intl.formatMessage({
              id: 'Load.RecipientsList.BatchSelectVisible',
            }),
            value: 'visible',
            onClick: handleVisible,
          },
          {
            label: intl.formatMessage({
              id: 'Load.RecipientsList.BatchSelectNone',
            }),
            value: 'none',
            onClick: handleNone,
          },
        ]}
      />
    </StyledContainer>,
    portalEl,
  )
}

const ButtonWithMenu = makeButtonWithMenu(TriggerComponent, MenuComponent)

interface ItemSelectAllProps {
  className?: string
}

const ItemSelectAll: FC<ItemSelectAllProps> = ({ className }) => {
  return <ButtonWithMenu className={className} direction="down" />
}

// export default ItemSelectAll

const StyledItemSelectAll = styled(ItemSelectAll)`
  transition: background-color 0.2s ease;

  padding: 3px 4px 3px 3px;
  margin: 0 0 0 -3px;

  border-radius: 4px;

  cursor: pointer;

  &:hover {
    background-color: #eeeeee;
  }

  & .c-checkbox__label::before {
    margin-right: 0;
  }
`

export default StyledItemSelectAll
